import React, { useEffect, useState } from "react";
import { api, formatDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Mail, MapPin, KeyRound, LockOpen, Pencil, Send } from "lucide-react";
import AdminAssignments from "@/pages/AdminAssignments";
import { ResetElectionPanel } from "@/components/ResetElectionPanel";
import { useAuth } from "@/context/AuthContext";
import { PermissionsOverview } from "@/components/PermissionsOverview";
import { BulkInvitePanel } from "@/components/BulkInvitePanel";
import { DomainStatusCard } from "@/components/DomainStatusCard";
import { RehearsalScoreboard } from "@/components/RehearsalScoreboard";

const inviteStatus = (u) => {
  if (u.invite_accepted_at || u.last_login_at) return { label: "Active", cls: "bg-emerald-500/15 text-emerald-300", title: `Signed in / accepted ${(u.invite_accepted_at || u.last_login_at).slice(0, 10)}` };
  if (u.invite_sent_at) return { label: "Invited", cls: "bg-amber-500/15 text-amber-300", title: `Invite sent ${u.invite_sent_at.slice(0, 10)} — not yet accepted` };
  return { label: "No invite", cls: "bg-white/5 text-zinc-500", title: "Never invited and never signed in" };
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [ownerEmail, setOwnerEmail] = useState("");
  const { user: me, setUser: setMe } = useAuth();
  const isOwner = !!me?.is_owner;
  const [lockouts, setLockouts] = useState({});
  const [form, setForm] = useState({ email: "", name: "", password: "", phone: "", role: "agent", send_invite: true });
  const [assignFor, setAssignFor] = useState(null); // user object we're editing assignments for

  const load = async () => {
    const [{ data }, lk, ow] = await Promise.all([api.get("/users"), api.get("/users/lockouts").catch(() => ({ data: {} })), api.get("/system/owner").catch(() => ({ data: {} }))]);
    setUsers(data);
    setLockouts(lk.data || {});
    setOwnerEmail((ow.data?.email || "").toLowerCase());
  };
  useEffect(() => { load(); }, []);

  const unlock = async (u) => {
    try { await api.post(`/users/${u.id}/unlock`); toast.success(`${u.email} unlocked`); load(); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Unlock failed"); }
  };

  const [editing, setEditing] = useState(null); // {id, name}
  const resendInvite = async (u) => {
    try { await api.post(`/users/${u.id}/resend-invite`); toast.success(`Invite with set-password link sent to ${u.email}`); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Could not send invite"); }
  };
  const changeRole = async (u, role) => {
    const label = role === "observer" ? "Reviewer" : "Agent";
    if (!window.confirm(`Switch ${u.email} to ${label}?${role !== "agent" ? " Their polling-unit assignments will be cleared." : ""} They will be signed out and emailed about the change.`)) return;
    try { await api.patch(`/users/${u.id}/role`, { role }); toast.success(`${u.email} is now a ${label}`); load(); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Role change failed"); }
  };
  const canRename = (u) => u.role !== "admin" || u.id === me?.id || isOwner;
  const saveName = async () => {
    if (!editing) return;
    try {
      await api.patch(`/users/${editing.id}/name`, { name: editing.name });
      toast.success("Name updated");
      if (editing.id === me?.id) setMe({ ...me, name: editing.name.trim() });
      setEditing(null); load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Rename failed"); }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/users", form);
      if (form.send_invite) {
        if (data.invite_sent) toast.success(`User created — invite with set-password link emailed to ${form.email}`);
        else toast.warning(`User created, but the invite email failed${data.invite_error ? `: ${data.invite_error}` : ""}. Use the resend button or share credentials manually.`);
      } else {
        toast.success("User created");
      }
      setForm({ email: "", name: "", password: "", phone: "", role: "agent", send_invite: true });
      load();
    } catch (e2) { toast.error(formatDetail(e2.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const resetPassword = async (u) => {
    if (!confirm(`Rotate password for ${u.email}?\nWe'll email them a new temporary password.`)) return;
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      if (data.emailed) toast.success(`New password sent to ${u.email}`);
      else toast.warning(`Password rotated. Email failed — share this with them: ${data.temporary_password}`, { duration: 15000 });
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Reset failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1200px]">
      <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Access Control</div>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2" data-testid="users-title">Users</h1>

      <form onSubmit={create} className="border border-white/10 bg-[#111114] p-5 mt-8 space-y-4" data-testid="create-user-form">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <input required placeholder="Email" type="email" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} className="bg-transparent border-b border-white/20 py-2 outline-none focus:border-white text-sm" data-testid="user-email-input"/>
          <input required placeholder="Full Name" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className="bg-transparent border-b border-white/20 py-2 outline-none focus:border-white text-sm" data-testid="user-name-input"/>
          <input placeholder="Phone (E.164, e.g. +23324…)" value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} className="bg-transparent border-b border-white/20 py-2 outline-none focus:border-white text-sm" data-testid="user-phone-input"/>
          <input required placeholder="Temporary Password" type="text" value={form.password} onChange={(e)=>setForm({...form, password:e.target.value})} className="bg-transparent border-b border-white/20 py-2 outline-none focus:border-white text-sm" data-testid="user-password-input"/>
          <select value={form.role} onChange={(e)=>setForm({...form, role:e.target.value})} className="bg-transparent border border-white/20 px-3 py-2 text-sm outline-none" data-testid="user-role-select">
            <option value="admin" className="bg-black">Admin</option>
            <option value="agent" className="bg-black">Agent</option>
            <option value="observer" className="bg-black">Reviewer</option>
          </select>
          <button type="submit" className="bg-white text-black text-xs uppercase tracking-[0.2em] font-semibold px-4 py-2 hover:bg-zinc-200 flex items-center justify-center gap-2" data-testid="create-user-btn"><Plus size={14}/>Create</button>
        </div>
        <div className="text-[11px] text-zinc-500">A phone number is required to receive Broadcast SMS alerts. Use full international format (E.164).</div>
        <label className="flex items-center gap-3 cursor-pointer text-xs text-zinc-400 select-none" data-testid="send-invite-label">
          <input type="checkbox" checked={form.send_invite} onChange={(e)=>setForm({...form, send_invite: e.target.checked})} data-testid="send-invite-checkbox" className="accent-emerald-500 w-4 h-4"/>
          <Mail size={13} strokeWidth={1.5}/>
          <span>Email an invitation with a personal <span className="text-emerald-300">set-your-password link</span> (valid 72 h). <span className="text-zinc-500">They choose their own password and can sign in immediately — no need to share the one above.</span></span>
        </label>
      </form>

      <div className="border border-white/10 mt-6 overflow-x-auto" data-testid="users-table-wrap">
        <div className="min-w-[860px]">
        <div className="grid grid-cols-12 px-4 py-3 border-b border-white/10 text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          <div className="col-span-3">Email</div>
          <div className="col-span-2">Name</div>
          <div className="col-span-2">Phone</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-2">Created · Status</div>
          <div className="col-span-1 text-right">—</div>
        </div>
        <div className="divide-y divide-white/5">
          {users.map(u => (
            <div key={u.id} className="grid grid-cols-12 px-4 py-3 items-center hover:bg-white/[0.02]">
              <div className="col-span-3 text-sm truncate flex items-center gap-2">
                <span className="truncate">{u.email}</span>
                {u.email.toLowerCase() === ownerEmail && <span className="text-[9px] uppercase tracking-wider bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 shrink-0" title="System owner — only this account can rotate passwords, delete admins or reset the system" data-testid={`owner-badge-${u.id}`}>Owner</span>}
                {lockouts[u.email]?.locked && <span className="text-[9px] uppercase tracking-wider bg-red-500/20 text-red-300 px-1.5 py-0.5 shrink-0" data-testid={`locked-badge-${u.id}`}>Locked</span>}
                {!lockouts[u.email]?.locked && lockouts[u.email]?.attempts > 0 && <span className="text-[9px] uppercase tracking-wider text-amber-400/80 shrink-0" data-testid={`attempts-badge-${u.id}`}>{lockouts[u.email].attempts} failed</span>}
              </div>
              <div className="col-span-2 text-sm group/name flex items-center gap-1 min-w-0">
                {editing?.id === u.id ? (
                  <input autoFocus value={editing.name} onChange={(e)=>setEditing({ ...editing, name: e.target.value })}
                    onKeyDown={(e)=>{ if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditing(null); }}
                    onBlur={saveName}
                    className="bg-transparent border-b border-emerald-400 py-0.5 outline-none text-sm w-full" data-testid={`name-input-${u.id}`}/>
                ) : (
                  <>
                    <span className="truncate">{u.name}</span>
                    {canRename(u) && (
                      <button onClick={()=>setEditing({ id: u.id, name: u.name || "" })} title="Edit name"
                        className="text-zinc-600 hover:text-white opacity-0 group-hover/name:opacity-100 p-0.5 shrink-0" data-testid={`edit-name-${u.id}`}>
                        <Pencil size={12}/>
                      </button>
                    )}
                  </>
                )}
              </div>
              <div className="col-span-2 text-xs tabular text-zinc-400 truncate">{u.phone || <span className="text-zinc-600">—</span>}</div>
              <div className="col-span-2 text-xs uppercase tracking-wider">
                {isOwner && u.role !== "admin" ? (
                  <select value={u.role} onChange={(e)=>changeRole(u, e.target.value)}
                    className="bg-transparent border border-white/15 hover:border-white/40 px-2 py-1 text-xs uppercase tracking-wider outline-none cursor-pointer" data-testid={`role-select-${u.id}`}>
                    <option value="agent" className="bg-black">Agent</option>
                    <option value="observer" className="bg-black">Reviewer</option>
                  </select>
                ) : (u.role === "observer" ? "Reviewer" : u.role)}
              </div>
              <div className="col-span-2 text-xs text-zinc-500 flex items-center gap-2">
                <span>{(u.created_at||"").slice(0,10)}</span>
                {(() => { const s = inviteStatus(u); return <span title={s.title} className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 ${s.cls}`} data-testid={`invite-status-${u.id}`}>{s.label}</span>; })()}
              </div>
              <div className="col-span-1 text-right flex items-center justify-end gap-1">
                <button onClick={()=>resendInvite(u)} title="Resend invite (set-password link)"
                  className="text-sky-400/70 hover:text-sky-300 p-1" data-testid={`resend-invite-${u.id}`}>
                  <Send size={14}/>
                </button>
                {lockouts[u.email] && (
                  <button onClick={()=>unlock(u)} title="Clear lockout / failed attempts"
                    className="text-red-400/80 hover:text-red-300 p-1" data-testid={`unlock-user-${u.id}`}>
                    <LockOpen size={14}/>
                  </button>
                )}
                {isOwner && (
                  <button onClick={()=>resetPassword(u)} title="Rotate password + email"
                    className="text-amber-400/70 hover:text-amber-300 p-1" data-testid={`reset-password-${u.id}`}>
                    <KeyRound size={14}/>
                  </button>
                )}
                {u.role === "agent" && isOwner && (
                  <button onClick={()=>setAssignFor(u)} title="Manage polling-unit assignments"
                    className="text-emerald-400/70 hover:text-emerald-300 p-1" data-testid={`assign-user-${u.id}`}>
                    <MapPin size={14}/>
                  </button>
                )}
                {(u.email.toLowerCase() !== ownerEmail && (isOwner || u.role !== "admin")) && (
                  <button onClick={()=>remove(u.id)} className="text-zinc-500 hover:text-red-400 p-1" data-testid={`delete-user-${u.id}`}><Trash2 size={14}/></button>
                )}
              </div>
            </div>
          ))}
          {!users.length && <div className="p-6 text-zinc-500 text-sm">No users.</div>}
        </div>
        </div>
      </div>
      {assignFor && <AdminAssignments user={assignFor} onClose={()=>setAssignFor(null)}/>}
      <PermissionsOverview ownerEmail={ownerEmail}/>
      {isOwner && <DomainStatusCard/>}
      {isOwner && <RehearsalScoreboard/>}
      {isOwner && <BulkInvitePanel onDone={load}/>}
      <ResetElectionPanel onDone={load} isOwner={isOwner}/>
    </div>
  );
}
